import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { ContactHero } from "@/components/site/contact/ContactHero";
import { ContactInfo } from "@/components/site/contact/ContactInfo";
import { ContactMap } from "@/components/site/contact/ContactMap";
import { ContactForm } from "@/components/site/contact/ContactForm";
import { UsefulInfo } from "@/components/site/contact/UsefulInfo";
import { SocialSection } from "@/components/site/contact/SocialSection";
import { ContactCTA } from "@/components/site/contact/ContactCTA";
import { BUSINESS, CAMPGROUND_ID, CONTACT_PATH, FACEBOOK_URL } from "@/lib/site";
import { PHOTOS } from "@/components/site/images";

const TITLE = "Contact Amigo Inn & RV Park | Zapata, Texas";
const DESCRIPTION =
  "Contact Amigo Inn & RV Park in Zapata, TX about RV sites, cabins, rooms, extended stays, group accommodations, reservations, or visits near Falcon Lake.";
const CANONICAL = `https://amigoinn-rvpark.com${CONTACT_PATH}`;
const heroImage = PHOTOS.sign;
const OG_IMAGE = `https://amigoinn-rvpark.com${heroImage.src}`;

export const Route = createFileRoute("/contact-us")({
  component: ContactUsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "preload",
        as: "image",
        href: heroImage.src,
        imageSrcSet: heroImage.srcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: TITLE,
          url: CANONICAL,
          description: DESCRIPTION,
          image: OG_IMAGE,
          isPartOf: {
            "@type": "WebSite",
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
          },
          about: { "@id": CAMPGROUND_ID },
          mainEntity: {
            "@id": CAMPGROUND_ID,
            "@type": ["Campground", "LodgingBusiness"],
            name: BUSINESS.name,
            url: BUSINESS.siteUrl,
            telephone: BUSINESS.phone,
            email: BUSINESS.email,
            sameAs: [FACEBOOK_URL],
            address: {
              "@type": "PostalAddress",
              streetAddress: BUSINESS.street,
              addressLocality: BUSINESS.city,
              addressRegion: BUSINESS.region,
              postalCode: BUSINESS.postalCode,
              addressCountry: BUSINESS.country,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: BUSINESS.siteUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Contact Us",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function ContactUsPage() {
  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main" className="pb-20 sm:pb-0">
        <ContactHero />

        <section
          className="texture-paper bg-background py-24 sm:py-28 lg:py-32"
          aria-labelledby="contact-info-title"
        >
          <div className="mx-auto grid max-w-[1400px] gap-14 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-start lg:gap-16 lg:px-10">
            <div>
              <ContactInfo />
              <ContactMap />
            </div>
            <ContactForm />
          </div>
        </section>

        <UsefulInfo />
        <SocialSection />
        <ContactCTA />
      </main>

      <SiteFooter
        newsletterHeading="Stay Connected With Amigo Inn"
        newsletterDescription="Receive park news, seasonal information, event announcements, and updates from Amigo Inn & RV Park."
      />
      <MobileBookingBar />
    </div>
  );
}
